import { useTranslation } from 'react-i18next';
import { useOnboardingPage } from './Onboarding.hooks';
import GreenCheckIcon from '@app/assets/icons/green-round-check.svg?react';
import BulbIcon from '@app/assets/icons/idea-icon.svg?react';
import { useNavigationContext } from '@app/contexts/NavigationContext';
import { OnboardingStep } from '@app/services/onboarding';

export const Onboarding = () => {
  const { t } = useTranslation();
  const { interceptNavigation } = useNavigationContext();
  const { 
    onboardingData, 
    isLoading, 
    error,
    handleStepAction,
    handleStepNavigation,
    isCompletingStep
  } = useOnboardingPage();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {t('onboarding.error.title', 'Something went wrong')}
          </h2>
          <p className="text-gray-600">
            {t('onboarding.error.message', 'Failed to load onboarding steps. Please try again.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('onboarding.title', 'Welcome to EchoRatings')}
          </h1>
          <p className="text-lg text-gray-600">
            {t('onboarding.subtitle', 'Follow these steps to complete the service setup and improve your experience')}
          </p>
        </div>

        {/* Progress Section */}
        <div className="bg-white shadow-sm p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('onboarding.progress.title', 'Get started now!')}
            </h2>
            <span className="text-sm text-gray-500">
              {onboardingData?.completionPercentage || 0}%
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-brand-baseBlue h-2 rounded-full transition-all duration-300"
              style={{ width: `${onboardingData?.completionPercentage || 0}%` }}
            ></div>
          </div>
        </div>

        {/* Onboarding Steps */}
        <div className="bg-white  shadow-sm overflow-hidden">
          {onboardingData?.steps?.map((step: OnboardingStep, index: number) => (
            <div key={step.id} className={`p-6 ${step.completed ? 'opacity-75' : ''} ${index < (onboardingData?.steps?.length || 0) - 1 ? 'border-b border-gray-200' : ''}`}>
              <div className="flex items-start gap-4">
                {/* Step Icon */}
                <div className="flex-shrink-0 mt-1">
                  {step.completed ? (
                    <div className="w-10 h-10 bg-[#F1FAF1] rounded-md flex items-center justify-center">
                      <GreenCheckIcon className="w-5 h-5" />
                    </div>
                  ) : (
                    <button
                      onClick={() => handleStepAction(step.id)}
                      disabled={isCompletingStep}
                      className="w-6 h-6 border-2 border-gray-300 rounded-full hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Mark as completed"
                    ></button>
                  )}
                </div>

                {/* Step Content */}
                <div className="flex-1">
                  <h3 className={`text-lg font-semibold mb-1 ${step.completed ? 'text-gray-500' : 'text-gray-900'}`}>
                    {step.name}
                  </h3>
                  <p className={`mb-3 ${step.completed ? 'text-gray-400' : 'text-gray-600'}`}>
                    {step.description}
                  </p>
                </div>

                {/* Action Button */}
                {!step.completed && (
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => handleStepNavigation(step.id)}
                      className="px-4 py-2 bg-brand-baseBlue text-white rounded-lg text-sm font-medium"
                    >
                      {t('onboarding.step.open', 'Open')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Upgrade Plan Section */}
        <div className="mt-8 bg-blue-50 border border-brand-baseBlue rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center">
              <BulbIcon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg text-gray-900 mb-1">
                {t('onboarding.upgrade.title', 'Upgrade your plan to unlock more features')}
              </h3>
              <button
                onClick={() => interceptNavigation('/settings?tab=plan')}
                className="text-[#3279C5] underline text-sm font-semibold"
              >
                {t('onboarding.upgrade.link', 'See plan')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
